import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
};

const COOKIE_KEY = 'taxely_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // always true
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      setTimeout(() => setVisible(true), 800);
    }
  }, []);

  const saveAndClose = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
  };

  const acceptAll = () => {
    saveAndClose({ essential: true, analytics: true, functional: true });
  };

  const rejectAll = () => {
    saveAndClose({ essential: true, analytics: false, functional: false });
  };

  const saveSelected = () => {
    saveAndClose(preferences);
  };

  if (!visible) return null;

  return (
    <div className={`cookie-consent ${showDetails ? 'cookie-consent--expanded' : ''}`}>
      <div className="cookie-consent__inner">

        {/* Header */}
        <div className="cookie-consent__header">
          <div className="cookie-consent__icon">🍪</div>
          <div>
            <h3 className="cookie-consent__title">Preferințe Cookie-uri</h3>
            <p className="cookie-consent__subtitle">
              Utilizăm cookie-uri pentru a îmbunătăți experiența ta pe platformă.
            </p>
          </div>
        </div>

        {/* Simple view */}
        {!showDetails && (
          <p className="cookie-consent__text">
            Folosim cookie-uri esențiale pentru funcționarea platformei și, cu acordul tău, cookie-uri de analiză pentru îmbunătățirea serviciilor. Poți afla mai multe în{' '}
            <Link to="/page/cookie" className="cookie-consent__link">
              Politica de Cookie-uri
            </Link>.
          </p>
        )}

        {/* Detailed view */}
        {showDetails && (
          <div className="cookie-consent__details">
            <div className="cookie-option">
              <div className="cookie-option__info">
                <div className="cookie-option__name">Cookie-uri Esențiale</div>
                <div className="cookie-option__desc">
                  Necesare pentru funcționarea platformei. Nu pot fi dezactivate.
                </div>
              </div>
              <div className="cookie-option__toggle cookie-option__toggle--always">
                Întotdeauna active
              </div>
            </div>

            <div className="cookie-option">
              <div className="cookie-option__info">
                <div className="cookie-option__name">Cookie-uri Funcționale</div>
                <div className="cookie-option__desc">
                  Rețin preferințele tale (limbă, setări) pentru o experiență personalizată.
                </div>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences(p => ({ ...p, functional: e.target.checked }))}
                />
                <span className="cookie-toggle__slider" />
              </label>
            </div>

            <div className="cookie-option">
              <div className="cookie-option__info">
                <div className="cookie-option__name">Cookie-uri de Analiză</div>
                <div className="cookie-option__desc">
                  Ne ajută să înțelegem cum este utilizată platforma (Google Analytics, date anonimizate).
                </div>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                />
                <span className="cookie-toggle__slider" />
              </label>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="cookie-consent__actions">
          <button
            className="cookie-consent__btn cookie-consent__btn--ghost"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Înapoi' : 'Personalizează'}
          </button>

          {showDetails ? (
            <>
              <button
                className="cookie-consent__btn cookie-consent__btn--outline"
                onClick={rejectAll}
              >
                Refuză toate
              </button>
              <button
                className="cookie-consent__btn cookie-consent__btn--primary"
                onClick={saveSelected}
              >
                Salvează preferințele
              </button>
            </>
          ) : (
            <>
              <button
                className="cookie-consent__btn cookie-consent__btn--outline"
                onClick={rejectAll}
              >
                Refuză
              </button>
              <button
                className="cookie-consent__btn cookie-consent__btn--primary"
                onClick={acceptAll}
              >
                Accept toate
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}